import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { ArrowUpRight } from 'lucide-react'
import Breadcrumb from '../components/Breadcrumb'
import { awards } from '../data/awards'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default function Awards() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.award-row')
      if (elements && elements.length > 0) {
        gsap.fromTo(
          elements,
          { x: -30, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.5,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top bottom',
              toggleActions: 'play none none none',
              invalidateOnRefresh: true,
            },
          }
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>Awards — Technifire Software</title>
        <meta name="description" content="Design and innovation awards won by Technifire for web design and development excellence. Recognized for outstanding digital projects." />
        <link rel="canonical" href="https://technifire.io/awards" />
        <meta property="og:title" content="Awards — Technifire Software" />
        <meta property="og:description" content="Design and innovation awards won by Technifire for web design and development excellence." />
        <meta property="og:url" content="https://technifire.io/awards" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Breadcrumb current="AWARDS" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-12">
        <h1 className="text-h2 uppercase text-white mb-6">
          AWARDS — INSPIRATION<br />FOR NEW PROJECTS
        </h1>
        <p className="text-body max-w-xl">
          To create high-quality and up-to-date websites, we constantly upgrade ourselves
          and explore new technologies and approaches.
        </p>
      </div>

      {/* Awards Table */}
      <div className="px-6 lg:px-10">
        {/* Header */}
        <div className="hidden lg:grid grid-cols-3 gap-4 py-4 border-b border-white/10 text-mono text-[#9A9A9A]">
          <span>PROJECT</span>
          <span>AWARD</span>
          <span className="text-right">YEAR</span>
        </div>

        {/* Rows */}
        {awards.map((award, index) => (
          <div
            key={award.id}
            className="award-row grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-4 py-5 border-b border-white/10 cursor-pointer hover:bg-white/[0.02] transition-colors group"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <span className="text-white text-lg group-hover:text-[#9A9A9A] transition-colors">
              {award.project}
            </span>
            <span className="text-[#9A9A9A] group-hover:text-white transition-colors">
              {award.award}
            </span>
            <div className="flex items-center justify-between lg:justify-end gap-4">
              <span className="text-mono text-[#9A9A9A]">{award.year}</span>
              <ArrowUpRight className="w-4 h-4 text-[#9A9A9A] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {/* Preview card on hover */}
            {hoveredIndex === index && (
              <div className="hidden lg:block absolute left-6 mt-12 w-64 bg-[#0a0a0a] rounded-xl p-4 shadow-2xl z-10">
                <div className="w-12 h-12 rounded-full bg-[#7dd3c0] flex items-center justify-center mb-3">
                  <span className="text-white font-bold text-lg">W.</span>
                </div>
                <p className="text-white text-sm font-medium">{award.award}</p>
                <p className="text-[#9A9A9A] text-xs mt-1">{award.year}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
